import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import {
    WemoloButton,
    WemoloChevronRightIcon,
    WemoloInput,
    WemoloLoading,
    WemoloRefreshIcon,
    WemoloSearchIcon,
    WemoloTypography
} from '../../../components/Wemolo'
import { DETECTION_EXPLORER_TEXT } from '../config'
import { formatDetectionTimestamp } from '../formatTimestamp'
import { ImageRequest } from '../list_image_requests'
import styles from './ImageRequestsPanel.module.css'

const TEXT = DETECTION_EXPLORER_TEXT.imageRequests

// Short, human description of what a request's selector targets — shown under the request id.
const selectorSummary = (selector: ImageRequest['detection_selector']): string =>
    selector.type === 'detection_uuids'
        ? TEXT.selectorDetections(selector.detection_uuids.length)
        : TEXT.selectorTimerange

// firstname.lastname@wemolo.com -> "FL". Falls back to the first two characters of the local
// part, or "?" when there's no requester. Display only — the full email shows on hover.
const requesterInitials = (email: string | null): string => {
    if (!email) return '?'
    const local = email.split('@')[0] ?? ''
    const parts = local.split('.').filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    return local.slice(0, 2).toUpperCase() || '?'
}

// `status` is a raw backend pass-through string. Known values get a friendly label and colour;
// anything else is shown verbatim with the neutral style.
const STATUS_META: Record<string, { label: string; className: string }> = {
    open: { label: TEXT.statusOpen, className: styles.statusOpen },
    partially_successful: { label: TEXT.statusPartial, className: styles.statusPartial },
    successful: { label: TEXT.statusSuccessful, className: styles.statusSuccessful },
    failed: { label: TEXT.statusFailed, className: styles.statusFailed }
}

const statusMeta = (status: string) => STATUS_META[status] ?? { label: status, className: styles.statusUnknown }

type Props = {
    requests: ImageRequest[]
    appliedRequestId: string | null
    onApply: (request: ImageRequest) => void
    onClose: () => void
    onRefresh: () => void
    isRefreshing: boolean
}

export const ImageRequestsPanel: React.FC<Props> = ({
    requests,
    appliedRequestId,
    onApply,
    onClose,
    onRefresh,
    isRefreshing
}) => {
    const [query, setQuery] = useState('')

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return requests
        return requests.filter((r) => {
            const haystack = `${r.request_uuid} ${r.requested_by ?? ''}`.toLowerCase()
            return haystack.includes(q)
        })
    }, [requests, query])

    return (
        <aside className={styles.root} aria-label={TEXT.title}>
            <div className={styles.header}>
                <span className={styles.titleRow} onClick={onClose}>
                    <WemoloTypography variant="label-small" tag="span" className={styles.title}>
                        {TEXT.title}
                    </WemoloTypography>
                </span>
                <button
                    type="button"
                    className={styles.headerButton}
                    aria-label={TEXT.refresh}
                    title={TEXT.refresh}
                    onClick={onRefresh}
                    disabled={isRefreshing}
                >
                    <WemoloRefreshIcon className={styles.headerIcon} />
                </button>
                <button
                    type="button"
                    className={styles.headerButton}
                    aria-label={TEXT.collapse}
                    title={TEXT.collapse}
                    onClick={onClose}
                >
                    <WemoloChevronRightIcon className={styles.headerIcon} />
                </button>
            </div>
            <div className={styles.search}>
                <WemoloInput
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={TEXT.searchPlaceholder}
                    leftIcon={<WemoloSearchIcon />}
                    autoComplete="off"
                />
            </div>
            <div className={styles.listWrap}>
                <div className={styles.list}>
                    {requests.length === 0 ? (
                        <WemoloTypography variant="body-small" tag="p" className={styles.empty}>
                            {TEXT.empty}
                        </WemoloTypography>
                    ) : filtered.length === 0 ? (
                        <WemoloTypography variant="body-small" tag="p" className={styles.empty}>
                            {TEXT.noMatches}
                        </WemoloTypography>
                    ) : (
                        filtered.map((request) => {
                            const status = statusMeta(request.status)
                            const isApplied = request.request_uuid === appliedRequestId
                            return (
                                <div
                                    key={request.request_uuid}
                                    className={classNames(styles.item, { [styles.itemApplied]: isApplied })}
                                >
                                    <span className={styles.avatar} tabIndex={0}>
                                        <WemoloTypography variant="label-small" tag="span">
                                            {requesterInitials(request.requested_by)}
                                        </WemoloTypography>
                                        <span role="tooltip" className={styles.avatarTooltip}>
                                            <WemoloTypography variant="label-small" tag="span">
                                                {request.requested_by ?? TEXT.unknownRequester}
                                            </WemoloTypography>
                                        </span>
                                    </span>
                                    <div className={styles.itemBody}>
                                        <span className={styles.requestId} title={request.request_uuid}>
                                            <WemoloTypography variant="label-xxsmall" tag="span">
                                                {request.request_uuid}
                                            </WemoloTypography>
                                        </span>
                                        <WemoloTypography variant="label-small" tag="span" className={styles.time}>
                                            {formatDetectionTimestamp(request.created_at)}
                                        </WemoloTypography>
                                        <WemoloTypography variant="label-small" tag="span" className={styles.selector}>
                                            {selectorSummary(request.detection_selector)}
                                        </WemoloTypography>
                                        <span className={classNames(styles.status, status.className)}>
                                            <WemoloTypography variant="label-small" tag="span">
                                                {status.label}
                                            </WemoloTypography>
                                        </span>
                                    </div>
                                    <WemoloButton
                                        view={isApplied ? 'secondary' : 'ghost'}
                                        size="small"
                                        className={styles.applyButton}
                                        aria-label={TEXT.applyTitle}
                                        onClick={() => onApply(request)}
                                    >
                                        {TEXT.apply}
                                    </WemoloButton>
                                </div>
                            )
                        })
                    )}
                </div>
                {isRefreshing && (
                    <div className={styles.overlay}>
                        <WemoloLoading />
                    </div>
                )}
            </div>
        </aside>
    )
}
